import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CHARGEE_ENTITY, type TermsDocument } from '@/content/terms/types'
import { formatProgramDate } from '@/lib/programs'

export interface RelatedDocument {
  label: string
  /** In-app path or full URL. */
  href: string
}

/**
 * A versioned legal document, readable without logging in so anyone can see
 * what they agree to before signing up. Printable; articles are numbered as the
 * text refers to them ("artikel 5").
 */
export function TermsPage({ document, related }: { document: TermsDocument; related: RelatedDocument[] }) {
  const { hash } = useLocation()

  // Deep links to an article ("#beloning") also work on first load.
  useEffect(() => {
    if (hash) window.document.getElementById(hash.slice(1))?.scrollIntoView()
    else window.scrollTo({ top: 0 })
  }, [hash, document])

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-10 pt-12">
      <article className="card mx-auto max-w-[820px] p-6 md:p-12 print:border-0 print:p-0">
        <header className="border-b border-beige-2 pb-6">
          <p className="text-13 font-bold uppercase tracking-wide text-dark-blue/50">Aanvullende voorwaarden</p>
          <h1 className="mt-2 text-28 font-bold leading-111 lg:text-42">{document.title}</h1>
          <p className="mt-3 text-15 text-dark-blue/60">
            Revisie {formatProgramDate(document.revisionDate)} · versie {document.version}
          </p>
          <div className="mt-5 flex flex-col gap-3 text-base leading-normal text-dark-blue/80">
            {document.intro.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </header>

        <nav aria-label="Inhoud" className="border-b border-beige-2 py-6 print:hidden">
          <p className="field-label">Inhoud</p>
          <ol className="mt-3 grid gap-x-6 gap-y-1.5 text-15 sm:grid-cols-2">
            {document.articles.map((a, i) => (
              <li key={a.id}>
                <a href={`#${a.id}`} className="text-dark-purple hover:underline">
                  {i + 1}. {a.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="flex flex-col gap-8 pt-8">
          {document.articles.map((a, i) => (
            <section key={a.id} id={a.id} className="scroll-mt-24">
              <h2 className="text-lg font-bold lg:text-xl">
                Artikel {i + 1}. {a.title}
              </h2>
              <div className="mt-3 flex flex-col gap-3 text-base leading-normal text-dark-blue/80">
                {a.blocks.map((block, j) =>
                  Array.isArray(block) ? (
                    <ul key={j} className="ml-5 flex list-disc flex-col gap-1.5 marker:text-dark-blue/40">
                      {block.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p key={j}>{block}</p>
                  )
                )}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-10 flex flex-col gap-4 border-t border-beige-2 pt-6 text-15 text-dark-blue/70">
          <div>
            <p className="font-semibold text-dark-blue">Hoort bij</p>
            <ul className="mt-2 flex flex-col gap-1">
              {related.map((r) => (
                <li key={r.href}>
                  {r.href.startsWith('/') ? (
                    <Link to={r.href} className="text-dark-purple underline">
                      {r.label}
                    </Link>
                  ) : (
                    <a href={r.href} target="_blank" rel="noreferrer" className="text-dark-purple underline">
                      {r.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <p>
            {CHARGEE_ENTITY.name} · {CHARGEE_ENTITY.address} · KvK {CHARGEE_ENTITY.kvk}
          </p>
          <button type="button" className="btn-secondary self-start print:hidden" onClick={() => window.print()}>
            Afdrukken of opslaan als pdf
          </button>
        </footer>
      </article>
    </div>
  )
}
